import { OpenAiOnboardingDocumentUpload } from './types';
import {
  handleOptionalResumeSchema,
  uploadProfilePictureSchema,
  uploadTechnicianDiplomaSchema,
  validateClientToolParameters,
} from './validation';
import { AiOnboardingDocumentType } from '../models/AiOnboardingModels';

interface DocumentUploadToolDependencies {
  openDocumentUpload: OpenAiOnboardingDocumentUpload;
}

type UploadErrorCode = 'UPLOAD_ERROR' | 'UPLOAD_NOT_COMPLETED';

const getUploadError = async (
  documentType: AiOnboardingDocumentType,
  openDocumentUpload: OpenAiOnboardingDocumentUpload,
): Promise<UploadErrorCode | null> => {
  try {
    const { uploaded } = await openDocumentUpload(documentType);
    return uploaded
      ? null
      : 'UPLOAD_NOT_COMPLETED';
  } catch {
    return 'UPLOAD_ERROR';
  }
};

export const createDocumentUploadClientTools = ({
  openDocumentUpload,
}: DocumentUploadToolDependencies) =>
  ({
    upload_profile_picture: async (parameters: unknown) => {
      const validation = await validateClientToolParameters(
        uploadProfilePictureSchema,
        parameters ?? {},
      );

      if (!validation.success) {
        return {
          success: false,
          profilePictureUploaded: false,
          errorCode: 'VALIDATION_ERROR',
        };
      }

      const uploadError = await getUploadError(
        'profilePicture',
        openDocumentUpload,
      );

      return uploadError
        ? {
          success: false,
          profilePictureUploaded: false,
          errorCode: uploadError,
        }
        : {
          success: true,
          profilePictureUploaded: true,
        };
    },

    handle_optional_resume: async (parameters: unknown) => {
      const validation = await validateClientToolParameters(
        handleOptionalResumeSchema,
        parameters,
      );

      if (!validation.success) {
        return {
          success: false,
          resumeUploaded: false,
          resumeSkipped: false,
          errorCode: 'VALIDATION_ERROR',
        };
      }

      const { shouldUpload } = validation.data.resumeData;
      const uploadError = shouldUpload
        ? await getUploadError('resume', openDocumentUpload)
        : null;

      return uploadError
        ? {
          success: false,
          resumeUploaded: false,
          resumeSkipped: false,
          errorCode: uploadError,
        }
        : {
          success: true,
          resumeUploaded: shouldUpload,
          resumeSkipped: !shouldUpload,
        };
    },

    upload_technician_diploma: async (parameters: unknown) => {
      const validation = await validateClientToolParameters(
        uploadTechnicianDiplomaSchema,
        parameters,
      );

      if (!validation.success) {
        return {
          success: false,
          diplomaUploaded: false,
          errorCode: 'VALIDATION_ERROR',
        };
      }

      const uploadError = await getUploadError(
        'diplomaCertificate',
        openDocumentUpload,
      );

      return uploadError
        ? {
          success: false,
          diplomaUploaded: false,
          errorCode: uploadError,
        }
        : {
          success: true,
          diplomaUploaded: true,
        };
    },
  });

export type DocumentUploadClientTools = ReturnType<
  typeof createDocumentUploadClientTools
>;
